import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Loader2, Save } from "lucide-react";
import { authApi, resolveImageUrl } from "@/api";
import { refreshSiteSettings } from "@/hooks/useSiteSettings";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import ImageFitControls from "@/components/admin/ImageFitControls";

function BlockEditor({ title, block, onChange, testid }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await authApi.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange({ ...block, image: resolveImageUrl(data.path) });
      toast.success("Image uploaded");
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const set = (k, v) => onChange({ ...block, [k]: v });

  return (
    <div className="bg-white border border-border p-6" data-testid={testid}>
      <h3 className="heading-3 mb-5">{title}</h3>
      <div className="grid md:grid-cols-[220px_1fr] gap-6">
        <div>
          <div className="aspect-square bg-secondary border border-border overflow-hidden flex items-center justify-center">
            {block.image ? <img src={block.image} alt="preview" className="w-full h-full" style={{ objectFit: block.image_fit || "cover", objectPosition: block.image_position || "center" }} /> : <span className="text-muted-foreground text-sm">No image</span>}
          </div>
          <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => upload(e.target.files[0])} data-testid={`${testid}-image-input`} />
          <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="w-full mt-2 border border-[#0A0A0A] py-2.5 label-caps flex items-center justify-center gap-2 btn-transition hover:bg-[#0A0A0A] hover:text-white disabled:opacity-60" data-testid={`${testid}-upload-btn`}>
            {uploading ? <><Loader2 className="h-4 w-4 animate-spin" /> Uploading…</> : <><Upload className="h-4 w-4" /> Upload</>}
          </button>
          <div className="mt-3">
            <ImageFitControls fit={block.image_fit} position={block.image_position} onChange={(p) => onChange({ ...block, ...p })} testidPrefix={`${testid}-image`} />
          </div>
        </div>
        <div className="space-y-4">
          <div>
            <Label className="label-caps text-muted-foreground">Kicker</Label>
            <Input value={block.kicker || ""} onChange={(e) => set("kicker", e.target.value)} className="mt-2 rounded-none" data-testid={`${testid}-kicker`} />
          </div>
          <div>
            <Label className="label-caps text-muted-foreground">Heading (Enter = new line)</Label>
            <Textarea rows={2} value={block.heading || ""} onChange={(e) => set("heading", e.target.value)} className="mt-2 rounded-none" data-testid={`${testid}-heading`} />
          </div>
          <div>
            <Label className="label-caps text-muted-foreground">Text</Label>
            <Textarea rows={3} value={block.text || ""} onChange={(e) => set("text", e.target.value)} className="mt-2 rounded-none" data-testid={`${testid}-text`} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="label-caps text-muted-foreground">Button Label</Label>
              <Input value={block.button_label || ""} onChange={(e) => set("button_label", e.target.value)} className="mt-2 rounded-none" data-testid={`${testid}-button-label`} />
            </div>
            <div>
              <Label className="label-caps text-muted-foreground">Button Link</Label>
              <Input value={block.button_link || ""} onChange={(e) => set("button_link", e.target.value)} className="mt-2 rounded-none" data-testid={`${testid}-button-link`} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const CATEGORY_TILES = [
  { slug: "drivers", label: "Drivers" },
  { slug: "fairway-woods", label: "Fairway Woods" },
  { slug: "hybrids", label: "Hybrids" },
  { slug: "irons", label: "Irons" },
  { slug: "wedges", label: "Wedges" },
  { slug: "putters", label: "Putters" },
];

function CategoryTileEditor({ slug, label, image, fit, position, onUpload, onFitChange }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const doUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await authApi.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUpload(slug, resolveImageUrl(data.path));
      toast.success(`${label} image updated`);
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };
  return (
    <div className="border border-border" data-testid={`cat-tile-${slug}`}>
      <div className="aspect-video bg-secondary overflow-hidden flex items-center justify-center">
        {image ? <img src={image} alt={label} className="w-full h-full" style={{ objectFit: fit || "cover", objectPosition: position || "center" }} /> : <span className="text-muted-foreground text-xs">No image</span>}
      </div>
      <div className="p-3 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <span className="label-caps">{label}</span>
          <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => doUpload(e.target.files[0])} data-testid={`cat-tile-input-${slug}`} />
          <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="border border-[#0A0A0A] px-3 py-1.5 label-caps flex items-center gap-1.5 btn-transition hover:bg-[#0A0A0A] hover:text-white disabled:opacity-60" data-testid={`cat-tile-upload-${slug}`}>
            {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Change
          </button>
        </div>
        <ImageFitControls fit={fit} position={position} onChange={(p) => onFitChange(slug, p)} testidPrefix={`cat-tile-${slug}`} />
      </div>
    </div>
  );
}

export default function AdminContent() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    authApi.get("/site-settings").then(({ data }) => setSettings(data)).catch(() => setSettings({ announcement: "", promo: {}, fitting: {}, tech_banner: {} }));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await authApi.put("/admin/site-settings", settings);
      await refreshSiteSettings();
      toast.success("Content saved");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  if (!settings) return <div className="p-10 text-center text-muted-foreground">Loading…</div>;

  return (
    <div className="max-w-4xl" data-testid="admin-content-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="heading-2">Site Content</h1>
          <p className="text-muted-foreground text-sm mt-1">Edit the announcement bar and homepage promo sections</p>
        </div>
        <button onClick={save} disabled={saving} className="bg-[#E31837] text-white px-6 py-3 label-caps flex items-center gap-2 btn-transition hover:bg-[#b3122c] active:scale-95 disabled:opacity-60" data-testid="save-content-btn">
          <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save Changes"}
        </button>
      </div>

      <div className="space-y-6">
        <div className="bg-white border border-border p-6" data-testid="announcement-editor">
          <h3 className="heading-3 mb-4">Announcement Bar</h3>
          <Label className="label-caps text-muted-foreground">Top strip text</Label>
          <Input value={settings.announcement || ""} onChange={(e) => setSettings((s) => ({ ...s, announcement: e.target.value }))} className="mt-2 rounded-none" data-testid="announcement-input" />
        </div>

        <BlockEditor title="Promo Section" block={settings.promo || {}} onChange={(b) => setSettings((s) => ({ ...s, promo: b }))} testid="promo-editor" />
        <BlockEditor title="Fitting CTA Section" block={settings.fitting || {}} onChange={(b) => setSettings((s) => ({ ...s, fitting: b }))} testid="fitting-editor" />
        <BlockEditor title='Homepage "Precision Is Not An Accident" Section' block={settings.tech_banner || {}} onChange={(b) => setSettings((s) => ({ ...s, tech_banner: b }))} testid="tech-banner-editor" />
        <BlockEditor title='Technology Page — "Science Of Better Golf" Hero Banner' block={settings.technology_banner || {}} onChange={(b) => setSettings((s) => ({ ...s, technology_banner: b }))} testid="technology-banner-editor" />

        <div className="bg-white border border-border p-6" data-testid="contact-whatsapp-editor">
          <h3 className="heading-3 mb-1">Contact &amp; WhatsApp</h3>
          <p className="text-muted-foreground text-sm mb-5">Phone number in the footer, WhatsApp CTAs on the home page, and the floating WhatsApp button.</p>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="label-caps text-muted-foreground">Display Phone Number</Label>
              <Input
                value={settings.phone_number || ""}
                onChange={(e) => setSettings((s) => ({ ...s, phone_number: e.target.value }))}
                placeholder="+91-9899039199"
                className="mt-2 rounded-none"
                data-testid="phone-number-input"
              />
              <p className="text-xs text-muted-foreground mt-1">Shown in the footer.</p>
            </div>
            <div>
              <Label className="label-caps text-muted-foreground">WhatsApp Number (digits only)</Label>
              <Input
                value={settings.whatsapp_number || ""}
                onChange={(e) => setSettings((s) => ({ ...s, whatsapp_number: e.target.value }))}
                placeholder="919899039199"
                className="mt-2 rounded-none"
                data-testid="whatsapp-number-input"
              />
              <p className="text-xs text-muted-foreground mt-1">International format, digits only (e.g. 919899039199).</p>
            </div>
          </div>
          <div className="mt-4">
            <Label className="label-caps text-muted-foreground">WhatsApp Prefilled Message</Label>
            <Input
              value={settings.whatsapp_message || ""}
              onChange={(e) => setSettings((s) => ({ ...s, whatsapp_message: e.target.value }))}
              placeholder="Hi PING India, I'd like to know more about your clubs."
              className="mt-2 rounded-none"
              data-testid="whatsapp-message-input"
            />
          </div>

          <div className="mt-6 border-t border-border pt-5">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={settings.whatsapp_strip_enabled !== false}
                onChange={(e) => setSettings((s) => ({ ...s, whatsapp_strip_enabled: e.target.checked }))}
                className="h-4 w-4 accent-[#25D366]"
                data-testid="whatsapp-strip-enabled"
              />
              <span className="font-semibold text-sm">Show WhatsApp strip on Home page</span>
            </label>
            <div className="mt-3 grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="label-caps text-muted-foreground">Strip Heading</Label>
                <Input value={settings.whatsapp_strip_heading || ""} onChange={(e) => setSettings((s) => ({ ...s, whatsapp_strip_heading: e.target.value }))} className="mt-2 rounded-none" data-testid="whatsapp-strip-heading-input" />
              </div>
              <div>
                <Label className="label-caps text-muted-foreground">Strip Sub-text</Label>
                <Input value={settings.whatsapp_strip_text || ""} onChange={(e) => setSettings((s) => ({ ...s, whatsapp_strip_text: e.target.value }))} className="mt-2 rounded-none" data-testid="whatsapp-strip-text-input" />
              </div>
            </div>
          </div>

          <div className="mt-5 border-t border-border pt-5">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={settings.whatsapp_float_enabled !== false}
                onChange={(e) => setSettings((s) => ({ ...s, whatsapp_float_enabled: e.target.checked }))}
                className="h-4 w-4 accent-[#25D366]"
                data-testid="whatsapp-float-enabled"
              />
              <span className="font-semibold text-sm">Show floating WhatsApp button (bottom-right, all pages)</span>
            </label>
          </div>
        </div>

        <div className="bg-white border border-border p-6" data-testid="ping-history-video-editor">
          <h3 className="heading-3 mb-1">History of PING — Video</h3>
          <p className="text-muted-foreground text-sm mb-4">Paste any YouTube link (regular watch URL, youtu.be, shorts) or Vimeo URL — it will be auto-normalised to an embeddable form.</p>
          <Label className="label-caps text-muted-foreground">Embed URL</Label>
          <Input
            value={settings.ping_history_video_url || ""}
            onChange={(e) => setSettings((s) => ({ ...s, ping_history_video_url: e.target.value }))}
            placeholder="https://www.youtube.com/embed/dQw4w9WgXcQ"
            className="mt-2 rounded-none"
            data-testid="ping-history-video-input"
          />
          {settings.ping_history_video_url && (
            <div className="mt-4 aspect-video max-w-lg border border-border bg-black">
              <iframe
                src={settings.ping_history_video_url}
                title="Preview"
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                data-testid="ping-history-video-preview"
              />
            </div>
          )}
        </div>

        <div className="bg-white border border-border p-6" data-testid="category-tiles-editor">
          <h3 className="heading-3 mb-1">Homepage Category Tiles</h3>
          <p className="text-muted-foreground text-sm mb-5">The images shown under "Built To Be Fit". Changes save when you click Save Changes above.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {CATEGORY_TILES.map((c) => (
              <CategoryTileEditor
                key={c.slug}
                slug={c.slug}
                label={c.label}
                image={settings.category_images?.[c.slug]}
                fit={settings.category_image_fits?.[c.slug]}
                position={settings.category_image_positions?.[c.slug]}
                onUpload={(slug, url) => setSettings((s) => ({ ...s, category_images: { ...(s.category_images || {}), [slug]: url } }))}
                onFitChange={(slug, patch) => setSettings((s) => {
                  const nextFits = { ...(s.category_image_fits || {}) };
                  const nextPos = { ...(s.category_image_positions || {}) };
                  if (patch.image_fit != null) nextFits[slug] = patch.image_fit;
                  if (patch.image_position != null) nextPos[slug] = patch.image_position;
                  return { ...s, category_image_fits: nextFits, category_image_positions: nextPos };
                })}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
